import axios from "axios";
import { AUTH_ENDPOINTS, API_BASE_URL } from "../constants/apis";
import tokenRefreshService from "./tokenRefresh";

// Authentication service
class AuthService {
    constructor() {
        this.setupInterceptors();
    }

    // Setup axios interceptors for automatic token refresh
    setupInterceptors() {
        // Request interceptor to add token to requests
        axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('accessToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor for token refresh
        axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        const newToken = await tokenRefreshService.getValidToken();
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axios(originalRequest);
                    } catch (refreshError) {
                        console.error('Token refresh failed:', refreshError);
                        this.logout();
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    // Login user
    async login(credentials) {
        try {
            const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.LOGIN}`, credentials);
            const { access_token, refresh_token, user } = response.data;

            // Store tokens
            localStorage.setItem('accessToken', access_token);
            if (refresh_token) {
                localStorage.setItem('refreshToken', refresh_token);
            }
            
            // Store user data
            if (user) {
                localStorage.setItem('userData', JSON.stringify(user));
            }

            return response.data;
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        }
    }

    // Register user
    async register(userData) {
        try {
            const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.REGISTER}`, userData);
            return response.data;
        } catch (error) {
            console.error('Registration failed:', error);
            throw error;
        }
    }

    // Logout user
    async logout() {
        try {
            const token = localStorage.getItem('accessToken');
            if (token) {
                await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.LOGOUT}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.error('Logout request failed:', error);
        } finally {
            // Clear all auth data regardless of API call success
            this.clearAuthData();
        }
    }

    // Clear all authentication data
    clearAuthData() {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('token'); // Legacy token
        localStorage.removeItem('userData');
    }

    // Check if user is authenticated
    isAuthenticated() {
        const token = localStorage.getItem('accessToken');
        return token && !tokenRefreshService.isTokenExpired(token);
    }

    // Get current user data
    getCurrentUser() {
        try {
            const userData = localStorage.getItem('userData');
            return userData ? JSON.parse(userData) : null;
        } catch (error) {
            console.error('Error parsing user data:', error);
            return null;
        }
    }

    // Get access token
    getAccessToken() {
        return localStorage.getItem('accessToken');
    }

    // Get refresh token
    getRefreshToken() {
        return localStorage.getItem('refreshToken');
    }

    // Refresh token manually
    async refreshToken() {
        return await tokenRefreshService.refreshToken();
    }

    // Forgot password
    async forgotPassword(email) {
        try {
            const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.FORGOT_PASSWORD}`, { email });
            return response.data;
        } catch (error) {
            console.error('Forgot password failed:', error);
            throw error;
        }
    }

    // Reset password
    async resetPassword(token, newPassword) {
        try {
            const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.RESET_PASSWORD}`, {
                token,
                password: newPassword
            });
            return response.data;
        } catch (error) {
            console.error('Reset password failed:', error);
            throw error;
        }
    }

    // Verify email
    async verifyEmail(token) {
        try {
            const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.VERIFY_EMAIL}`, { token });
            return response.data;
        } catch (error) {
            console.error('Email verification failed:', error);
            throw error;
        }
    }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
